import { createApiClient } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';

export interface TenantInvitation {
  id: string;
  tenantId: string;
  tenantName: string;
  invitedUserId: string;
  email: string;
  role: string;
  invitedByName: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string | null;
}

export interface TenantInvitationPreview {
  invitationId: string;
  tenantId: string;
  tenantName: string;
  invitedByName: string;
  role: string;
  expiresAt: string;
  currentTenantName: string;
}

export interface AcceptTenantInvitationResult {
  user: {
    id: string;
    tenantId: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  newTenantName: string;
}

export interface UnifiedInviteResult {
  flow: 'existing_user' | 'new_user_clerk' | 'existing_clerk_user';
  invitationId?: string;
  userId?: string;
  message?: string;
}

class TenantInvitationService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.users);
  }

  async listPending(tenantId: string): Promise<TenantInvitation[]> {
    const response = await this.api.get(`/api/tenants/${tenantId}/invitations`);
    return response.data;
  }

  async revoke(invitationId: string, tenantId: string): Promise<void> {
    await this.api.delete(`/api/tenants/${tenantId}/invitations/${invitationId}`);
  }

  async listMyPending(tenantId: string): Promise<TenantInvitationPreview[]> {
    const response = await this.api.get(`/api/tenants/${tenantId}/invitations/mine`);
    return response.data;
  }

  async acceptById(invitationId: string, tenantId: string): Promise<AcceptTenantInvitationResult> {
    const response = await this.api.post(`/api/tenants/${tenantId}/invitations/${invitationId}/accept`);
    return response.data;
  }

  async preview(token: string, tenantId: string): Promise<TenantInvitationPreview> {
    const response = await this.api.get(`/api/tenants/${tenantId}/invitations/preview?token=${encodeURIComponent(token)}`);
    return response.data;
  }

  async accept(token: string, tenantId: string): Promise<AcceptTenantInvitationResult> {
    const response = await this.api.post(`/api/tenants/${tenantId}/invitations/accept`, { token });
    return response.data;
  }
}

const tenantInvitationService = new TenantInvitationService();
export default tenantInvitationService;
